import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { AccountApiError, MAX_PROFILE_IMAGE_BYTES, PROFILE_IMAGE_TYPES, notifyUserImageUpdated, type AccountApi, type AuthSession, type Pal } from '../api'
import { UserAvatar } from './UserAvatar'
import './settings-dialog.css'

function palsError(error: unknown): string {
  if (error instanceof AccountApiError) return error.message
  return 'Something went wrong. Please try again.'
}

function PalsList({
  pals,
  emptyText,
  action,
}: {
  pals: Pal[]
  emptyText: string
  action: (pal: Pal) => React.ReactNode
}) {
  if (pals.length === 0) return <p className="profile-pals__empty">{emptyText}</p>
  return (
    <ul className="profile-pals__list">
      {pals.map((pal) => (
        <li key={`${pal.id}:${pal.username}`}>
          <UserAvatar username={pal.username} className="profile-pals__avatar" />
          <strong>{pal.username}</strong>
          {action(pal)}
        </li>
      ))}
    </ul>
  )
}

export function ProfileDialog({
  open,
  session,
  api,
  onLogout,
  onClose,
}: {
  open: boolean
  session: AuthSession
  api: AccountApi
  onLogout: () => void
  onClose: () => void
}) {
  const [pals, setPals] = useState<Pal[]>([])
  const [addedBy, setAddedBy] = useState<Pal[]>([])
  const [loading, setLoading] = useState(false)
  const [busyUsername, setBusyUsername] = useState<string>()
  const [confirmRemove, setConfirmRemove] = useState<string>()
  const [newPal, setNewPal] = useState('')
  const [notice, setNotice] = useState<string>()
  const [error, setError] = useState<string>()
  const [imageFile, setImageFile] = useState<File>()
  const [imagePreview, setImagePreview] = useState<string>()
  const [imageUploading, setImageUploading] = useState(false)
  const [imageError, setImageError] = useState<string>()
  const [imageNotice, setImageNotice] = useState<string>()
  const confirmationRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadPals = useCallback(async (signal?: AbortSignal) => {
    const [ownPals, invertedPals] = await Promise.all([
      api.listPals(session, false, signal),
      api.listPals(session, true, signal),
    ])
    setPals(ownPals)
    setAddedBy(invertedPals)
  }, [api, session])

  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    setLoading(true)
    setError(undefined)
    setNotice(undefined)
    setConfirmRemove(undefined)
    setImageFile(undefined)
    setImageError(undefined)
    setImageNotice(undefined)
    void loadPals(controller.signal).catch((reason: unknown) => {
      if (reason instanceof DOMException && reason.name === 'AbortError') return
      setError(palsError(reason))
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false)
    })
    return () => controller.abort()
  }, [loadPals, open])

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(undefined)
      return
    }
    const preview = URL.createObjectURL(imageFile)
    setImagePreview(preview)
    return () => URL.revokeObjectURL(preview)
  }, [imageFile])

  const palNames = useMemo(
    () => new Set(pals.map((pal) => pal.username.toLocaleLowerCase())),
    [pals],
  )

  useEffect(() => {
    if (!confirmRemove) return
    const frame = window.requestAnimationFrame(() => {
      confirmationRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [confirmRemove])

  const add = async (usernameOrEmail: string) => {
    const value = usernameOrEmail.trim()
    if (!value || busyUsername) return
    setBusyUsername(value)
    setError(undefined)
    setNotice(undefined)
    try {
      const message = await api.addPal(session, value)
      await loadPals()
      setNewPal('')
      setNotice(message ?? `${value} was added to your pals.`)
    } catch (reason) {
      setError(palsError(reason))
    } finally {
      setBusyUsername(undefined)
    }
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void add(newPal)
  }

  const remove = async (username: string) => {
    if (busyUsername) return
    setBusyUsername(username)
    setError(undefined)
    setNotice(undefined)
    try {
      await api.removePal(session, username)
      await loadPals()
      setConfirmRemove(undefined)
      setNotice(`${username} was removed from your pals.`)
    } catch (reason) {
      setError(palsError(reason))
    } finally {
      setBusyUsername(undefined)
    }
  }

  const selectImage = (file?: File) => {
    setImageFile(undefined)
    setImageError(undefined)
    setImageNotice(undefined)
    if (!file) return
    if (file.size > MAX_PROFILE_IMAGE_BYTES) {
      setImageError('The image must be 2 MB or smaller.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (!PROFILE_IMAGE_TYPES.includes(file.type as (typeof PROFILE_IMAGE_TYPES)[number])) {
      setImageError('Choose a JPG or PNG image.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setImageFile(file)
  }

  const uploadImage = async () => {
    if (!imageFile || imageUploading) return
    setImageUploading(true)
    setImageError(undefined)
    setImageNotice(undefined)
    try {
      await api.uploadProfileImage(session, imageFile)
      notifyUserImageUpdated(session.username)
      setImageFile(undefined)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setImageNotice('Your profile image was updated.')
    } catch (reason) {
      setImageError(palsError(reason))
    } finally {
      setImageUploading(false)
    }
  }

  if (!open) return null

  return (
    <div className="settings-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="settings-dialog profile-dialog" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <div className="settings-dialog__heading">
          <div>
            <p>Your account</p>
            <h2 id="profile-title">Profile settings</h2>
          </div>
          <button type="button" aria-label="Close profile settings" onClick={onClose}>×</button>
        </div>

        <div className="profile-dialog__identity">
          <UserAvatar username={session.username} className="profile-dialog__avatar" />
          <div>
            <strong>{session.username}</strong>
            <span>Signed in</span>
            {session.email ? <a href={`mailto:${session.email}`}>{session.email}</a> : null}
          </div>
        </div>

        <section className="profile-image-upload" aria-labelledby="profile-image-title">
          <div>
            <strong id="profile-image-title">Profile image</strong>
            <small>JPG or PNG, max 2 MB</small>
          </div>
          <input
            ref={fileInputRef}
            id="profile-image-file"
            type="file"
            accept="image/jpeg,image/png"
            disabled={imageUploading}
            onChange={(event) => selectImage(event.target.files?.[0])}
          />
          <label htmlFor="profile-image-file">Choose image</label>

          {imageFile && imagePreview ? (
            <div className="profile-image-upload__selection">
              <img src={imagePreview} alt="Selected profile preview" />
              <span><strong>{imageFile.name}</strong><small>{Math.max(1, Math.round(imageFile.size / 1024))} KB</small></span>
              <button type="button" disabled={imageUploading} onClick={() => void uploadImage()}>{imageUploading ? 'Uploading…' : 'Upload'}</button>
            </div>
          ) : null}
          {imageError ? <p className="profile-image-upload__message profile-image-upload__message--error" role="alert">{imageError}</p> : null}
          {imageNotice ? <p className="profile-image-upload__message profile-image-upload__message--success" role="status">{imageNotice}</p> : null}
        </section>

        <button className="profile-dialog__logout" type="button" onClick={onLogout}>Sign out</button>

        <section className="profile-pals" aria-labelledby="profile-pals-title">
          <div className="profile-pals__heading">
            <div>
              <p>Connections</p>
              <h3 id="profile-pals-title">Pals</h3>
            </div>
            {!loading ? <span>{pals.length} {pals.length === 1 ? 'pal' : 'pals'}</span> : null}
          </div>

          <form className="profile-pals__add" onSubmit={submit}>
            <label htmlFor="new-pal">Add a pal</label>
            <div>
              <input
                id="new-pal"
                type="text"
                value={newPal}
                autoComplete="off"
                placeholder="Username or email"
                disabled={Boolean(busyUsername)}
                onChange={(event) => setNewPal(event.target.value)}
              />
              <button type="submit" disabled={!newPal.trim() || Boolean(busyUsername)}>
                {busyUsername === newPal.trim() ? 'Adding…' : 'Add'}
              </button>
            </div>
            <small>Pals-only trips become visible when you have both added each other.</small>
          </form>

          {error ? <p className="profile-pals__message profile-pals__message--error" role="alert">{error}</p> : null}
          {notice ? <p className="profile-pals__message profile-pals__message--success" role="status">{notice}</p> : null}

          {loading ? (
            <div className="profile-pals__loading" aria-label="Loading pals">
              <span /><span /><span />
            </div>
          ) : (
            <>
              <section className="profile-pals__group" aria-labelledby="your-pals-title">
                <h4 id="your-pals-title">Your pals</h4>
                <PalsList
                  pals={pals}
                  emptyText="You have not added any pals yet."
                  action={(pal) => (
                    <button type="button" className="profile-pals__remove" disabled={Boolean(busyUsername)} onClick={() => setConfirmRemove(pal.username)}>
                      Remove
                    </button>
                  )}
                />
              </section>

              {confirmRemove ? (
                <div ref={confirmationRef} className="profile-pals__confirmation" role="alertdialog" aria-labelledby="remove-pal-title">
                  <div><strong id="remove-pal-title">Remove {confirmRemove}?</strong><span>They will no longer be one of your pals.</span></div>
                  <button type="button" disabled={Boolean(busyUsername)} onClick={() => setConfirmRemove(undefined)}>Cancel</button>
                  <button type="button" className="profile-pals__confirm-remove" disabled={Boolean(busyUsername)} onClick={() => void remove(confirmRemove)}>
                    {busyUsername === confirmRemove ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              ) : null}

              <section className="profile-pals__group" aria-labelledby="added-you-title">
                <div className="profile-pals__group-heading">
                  <h4 id="added-you-title">People who added you</h4>
                  <small>Add them back to become mutual pals and see each other's pals-only trips.</small>
                </div>
                <PalsList
                  pals={addedBy}
                  emptyText="No one has added you as a pal yet."
                  action={(pal) => palNames.has(pal.username.toLocaleLowerCase()) ? (
                    <span className="profile-pals__mutual">Mutual pals</span>
                  ) : (
                    <button type="button" className="profile-pals__add-back" disabled={Boolean(busyUsername)} onClick={() => void add(pal.username)}>
                      {busyUsername === pal.username ? 'Adding…' : 'Add back'}
                    </button>
                  )}
                />
              </section>
            </>
          )}
        </section>

        <button className="settings-dialog__done" type="button" onClick={onClose}>Done</button>
      </section>
    </div>
  )
}
